<script setup lang="ts">
import { format, parseISO } from 'date-fns';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import PublicPageLayout from '../../components/PublicPageLayout.vue';
import RequestStatus from '../../components/RequestStatus.vue';
import { useRequestBase } from '../../composables/request.ts';
import { usePublicFlowAdapter } from '../adapter.ts';
import ActorIdentity from './ActorIdentity.vue';
import FlowAuthentication from './FlowAuthentication.vue';

type WebinarContext = {
    title: string;
    description: string | null;
    date: string;
    durationMinutes: number | null;
};

const route = useRoute();
const adapter = usePublicFlowAdapter();
const endpoint = computed(
    () => `${adapter.apiUrl}/webinar-certificates/${String(route.params.webinar)}`,
);
const contextRequest = useRequestBase<WebinarContext>(endpoint, { refetch: true }).json();
const certificateRequest = useRequestBase<{ url: string }>(endpoint, { immediate: false })
    .json()
    .post();
const isNavigating = ref(false);

certificateRequest.onFetchResponse((response) => {
    const url = certificateRequest.data.value?.url;

    if (response.ok && url) {
        isNavigating.value = true;

        const navigation = adapter.open(url);

        if (navigation) {
            void navigation.finally(() => (isNavigating.value = false));
        }
    }
});
</script>

<template>
    <RequestStatus :request="contextRequest">
        <PublicPageLayout
            v-if="contextRequest.data.value"
            :brand-url="adapter.brandUrl"
            title="Webinar Certificate"
        >
            <template #description>
                <h3>{{ contextRequest.data.value.title }}</h3>
                <p v-if="contextRequest.data.value.description" class="text-sm">
                    {{ contextRequest.data.value.description }}
                </p>
                <p class="text-sm">
                    {{ format(parseISO(contextRequest.data.value.date), 'd MMMM yyyy') }}
                    <template v-if="contextRequest.data.value.durationMinutes">
                        · {{ contextRequest.data.value.durationMinutes }} minutes
                    </template>
                </p>
            </template>

            <ActorIdentity v-if="adapter.actorEmail.value" />
            <FlowAuthentication v-else :intended="route.fullPath" />

            <template v-if="adapter.actorEmail.value" #footer>
                <button
                    type="button"
                    class="btn bg-primary btn--lg rounded-full full-width"
                    :class="{ 'is-loading': certificateRequest.isFetching.value || isNavigating }"
                    :disabled="certificateRequest.isFetching.value || isNavigating"
                    @click="certificateRequest.execute()"
                >
                    Download Certificate
                </button>
            </template>
        </PublicPageLayout>
    </RequestStatus>
</template>
